import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useEffect, useMemo } from 'react';
import { NavLink, useHistory, useLocation, useParams } from 'react-router-dom';
import { MastheadPortalId } from '../../../models/AdminModels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useAdminAccountQuery } from '../../../queries';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../../web/components/Breadcrumb';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { MultiContainerHeader } from '../../../web/components/MultiContainerHeader';
import { Button } from '../../../web/components-v2/Button';
import { AdminDrawerToolbar, SideDrawer } from '../../../web/components-v2/Drawer/AppDrawer';
import { useAutomationFlowTemplate } from '../../../web/containers/AutomationFlows/hooks/useAutomationFlowTemplate';
import { baseStyleSheet } from '../../../web/styles/styles';
import { AutomationCanvasEditor } from '../../../shared/components/automation/AutomationCanvasEditor';
import {
	IAutomationsReturnLocationState,
	resolveAutomationsReturnLocation,
} from '../../../shared/components/automation/automationsReturnLocation';
import { AdminDashboardPageLayout } from '../../containers/AdminDashboardPageLayout';
import { styleSheet } from './styles';

export function AdminAutomationCanvasPage() {
	const { id: templateId, accountId } = useParams<{ id: string; accountId: string }>();
	const history = useHistory();
	const location = useLocation<IAutomationsReturnLocationState | undefined>();
	const userSession = useUserSession();
	const accountAutomationsPath = `/accounts/${accountId}/automations`;

	const returnToAccountAutomations = () =>
		history.push(resolveAutomationsReturnLocation(location.state, accountAutomationsPath));

	// Fetch the impersonated account so the impersonation context carries full
	// account features (e.g. sendEmail.observeSendIntervals) for downstream consumers
	// like the schedule time-of-day dropdown — not just the id used for API routing.
	const accountQuery = useAdminAccountQuery({ accountId });
	const companyName = accountQuery.data?.companyName;
	const impersonationContext = useMemo<Api.IImpersonationContext | undefined>(() => {
		if (!accountId) return undefined;
		return { account: accountQuery.data ?? { id: accountId } };
	}, [accountId, accountQuery.data]);

	// Set global impersonation context on the user session.
	useEffect(() => {
		if (impersonationContext && userSession) {
			userSession.impersonate(impersonationContext);
		}
		return () => {
			if (userSession) {
				userSession.impersonate(null);
			}
		};
	}, [impersonationContext, userSession]);

	const { template, isLoading, isError, error, refetch } = useAutomationFlowTemplate({
		id: templateId,
		enabled: Boolean(templateId),
		impersonationContext,
	});

	return (
		<AutomationCanvasEditor
			templateId={templateId}
			template={template}
			isLoading={isLoading}
			isError={isError}
			error={error}
			refetch={refetch}
			impersonationContext={impersonationContext}
			onPublishSuccess={returnToAccountAutomations}
			onDeleteSuccess={returnToAccountAutomations}
			contentStyles={[styleSheet.contentExtras]}
			renderLoading={message => (
				<AdminDashboardPageLayout sidebarChildren={null}>
					<div className={css(styleSheet.centeredContainer)}>
						<LoadingSpinner type='large' />
						<p className={css(styleSheet.loadingText)}>{message}</p>
					</div>
				</AdminDashboardPageLayout>
			)}
			renderError={(message, onRetry) => (
				<AdminDashboardPageLayout sidebarChildren={null}>
					<div className={css(styleSheet.centeredContainer)}>
						<h2 className={css(styleSheet.errorTitle)}>Failed to load automation</h2>
						<p className={css(styleSheet.errorMessage)}>{message}</p>
						<Button variant='primary' onClick={onRetry}>
							Retry
						</Button>
					</div>
				</AdminDashboardPageLayout>
			)}
		>
			{({ contentArea, drawerPanels, drawerToolbarButtons }) => (
				<AdminDashboardPageLayout
					sidebarChildren={
						<SideDrawer>
							{drawerPanels}
							<AdminDrawerToolbar>{drawerToolbarButtons}</AdminDrawerToolbar>
						</SideDrawer>
					}
				>
					<MultiContainerHeader
						appBarHeader={
							<div className={css(baseStyleSheet.horizontalStack)} style={{ alignItems: 'center', gap: '16px' }}>
								<Breadcrumb>
									<BreadcrumbList>
										<BreadcrumbItem>
											<BreadcrumbLink asChild>
												<NavLink to='/accounts'>Accounts</NavLink>
											</BreadcrumbLink>
										</BreadcrumbItem>
										<BreadcrumbSeparator />
										<BreadcrumbItem>
											<BreadcrumbLink asChild>
												<NavLink to={accountAutomationsPath}>{companyName ?? 'Loading...'}</NavLink>
											</BreadcrumbLink>
										</BreadcrumbItem>
										<BreadcrumbSeparator />
										<BreadcrumbItem>
											<BreadcrumbPage>{`Canvas "${template?.name ?? '...'}"`}</BreadcrumbPage>
										</BreadcrumbItem>
									</BreadcrumbList>
								</Breadcrumb>
							</div>
						}
						appBarPortalDestinationId={MastheadPortalId}
					/>
					{contentArea}
				</AdminDashboardPageLayout>
			)}
		</AutomationCanvasEditor>
	);
}
